import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Logo from "@/components/Logo";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";
import { getDivision, DIVISIONS } from "@/data/divisions";

export default function DivisionDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const d = getDivision(slug);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  if (!d) {
    return (
      <div className="min-h-screen bg-konattu-cream flex flex-col items-center justify-center gap-4" data-testid="division-notfound">
        <p className="font-display text-2xl text-konattu-charcoal">Division not found.</p>
        <Link to="/" className="text-konattu-red font-mono text-sm tracking-widest">GO HOME</Link>
      </div>
    );
  }

  const others = DIVISIONS.filter((x) => x.slug !== slug);

  return (
    <main className="bg-konattu-cream" data-testid="division-detail-page">
      <div className="noise-overlay" />
      <div className="fixed top-0 inset-x-0 z-50 bg-konattu-cream/80 backdrop-blur-2xl border-b border-konattu-charcoal/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-[70px] flex items-center justify-between">
          <Link to="/" data-testid="division-logo"><Logo /></Link>
          <Link to="/" data-testid="division-back" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-konattu-charcoal/70 hover:text-konattu-red transition-colors">
            <ArrowLeft size={14} /> ALL DIVISIONS
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative min-h-[75vh] flex items-end overflow-hidden pt-[70px]" style={d.photos ? {} : { background: d.bg }} data-testid="division-hero">
        {d.photos && (
          <>
            <motion.img initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} src={d.hero} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-konattu-charcoal via-konattu-charcoal/60 to-konattu-charcoal/20" />
          </>
        )}
        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-10 pb-16 md:pb-24 w-full">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-mono text-xs tracking-[0.35em]" style={{ color: d.photos ? "#FBDE08" : d.accent }}>
            {d.kicker}
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, ease: [0.22, 1, 0.36, 1], duration: 0.9 }} className="mt-5 font-display font-extrabold text-[#F7F2E9] tracking-tightest leading-[0.9] text-5xl md:text-7xl lg:text-8xl">
            {d.name}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 font-display text-2xl md:text-3xl text-[#F7F2E9]/85">
            {d.tagline}
          </motion.p>
        </div>
      </section>

      {/* Intro + features */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <span className="font-mono text-xs tracking-[0.35em]" style={{ color: d.accent }}>/ OVERVIEW</span>
            <p className="mt-6 font-display font-medium text-2xl md:text-4xl text-konattu-charcoal tracking-tight leading-[1.15]">{d.intro}</p>
          </Reveal>
          <div className="lg:col-span-5 lg:pt-14 space-y-4">
            {d.features.map((f, i) => (
              <Reveal key={f} delay={i * 0.07}>
                <div className="flex items-center gap-3 border-b border-konattu-charcoal/12 pb-4" data-testid={`division-feature-${i}`}>
                  <Check size={18} style={{ color: d.accent }} />
                  <span className="font-body text-konattu-charcoal/85 text-lg">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="pb-24 md:pb-32" data-testid="division-products">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal><h2 className="font-display font-bold text-3xl md:text-5xl text-konattu-charcoal tracking-tight mb-12">What you&apos;ll find</h2></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-konattu-charcoal/12 border border-konattu-charcoal/12">
            {d.products.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div className="bg-konattu-cream p-8 md:p-10 h-full group hover:bg-white transition-colors" data-testid={`product-${i}`}>
                  <span className="font-mono text-xs" style={{ color: d.accent }}>{String(i + 1).padStart(2, "0")}</span>
                  <div className="mt-3 font-display font-semibold text-lg md:text-2xl text-konattu-charcoal tracking-tight">{p}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {d.photos && (
        <section className="pb-24 md:pb-32" data-testid="division-gallery">
          <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {d.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.06} className={i === 0 ? "col-span-2 md:col-span-2 md:row-span-2" : ""}>
                <div className={`overflow-hidden group ${i === 0 ? "aspect-square md:aspect-auto md:h-full" : "aspect-square"}`}>
                  <img src={g} alt={`${d.name} ${i + 1}`} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20" style={{ background: d.photos ? "#371D00" : d.bg }}>
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h3 className="font-display font-bold text-3xl md:text-5xl text-[#F7F2E9] tracking-tight max-w-2xl">Have a question about {d.name}?</h3>
          <div className="flex flex-wrap gap-4">
            <a href={`tel:${d.phone}`} data-testid="division-call" className="inline-flex items-center gap-3 border border-[#F7F2E9]/40 text-[#F7F2E9] px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#F7F2E9] hover:text-konattu-charcoal transition-colors">
              Call {d.phone}
            </a>
            <button onClick={() => nav("/#contact")} data-testid="division-cta" className="group inline-flex items-center gap-3 bg-[#F7F2E9] text-konattu-charcoal px-8 py-4 text-sm font-medium tracking-wide hover:bg-konattu-red hover:text-white transition-colors">
              Get in touch <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Other divisions */}
      <section className="py-20 bg-konattu-sand" data-testid="division-others">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <span className="font-mono text-xs tracking-[0.35em] text-konattu-red">/ EXPLORE MORE</span>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {others.map((o) => (
              <Link key={o.slug} to={`/division/${o.slug}`} data-testid={`other-${o.slug}`} className="group border border-konattu-charcoal/15 p-6 hover:border-konattu-red transition-colors bg-konattu-cream">
                <div className="font-mono text-[10px] tracking-[0.25em] text-konattu-charcoal/50">{o.kicker}</div>
                <div className="mt-3 font-display font-semibold text-xl text-konattu-charcoal group-hover:text-konattu-red transition-colors flex items-center justify-between">
                  {o.name} <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
